import React, { useState, useRef, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useWhitelistRole } from '../useWhitelistRole';
import { useStudioConfig, applyStudioConfig } from '../useStudioConfig';
import { supabase } from '../supabase';

const ASSETS_BUCKET = 'studio-assets';

function PencilIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.5 3.5l3 3L7 16H4v-3L13.5 3.5z" />
    </svg>
  );
}

const navItems = [
  { id: 'dashboard',    label: 'Dashboard',         icon: DashboardIcon },
  { id: 'leads',        label: 'Leads',             icon: LeadsIcon, adminOnly: true },
  { id: 'estadisticas', label: 'Estadísticas',      icon: ChartIcon, adminOnly: true },
  { id: 'manuales',     label: 'Manuales',          icon: BookIcon     },
  { id: 'journal',      label: 'Mi Journal',        icon: JournalIcon  },
  { id: 'equipo',       label: 'Equipo',            icon: EquipoIcon   },
  { id: 'etiquetas',    label: 'Etiquetas',         icon: EtiquetasIcon },
  { id: 'metrados',     label: 'Metrados y Costos', icon: MetradosIcon  },
  { id: 'gantt',        label: 'Gantt',             icon: GanttIcon    },
  { id: 'cronograma',   label: 'Cronograma de Proyectos', icon: CalendarIcon },
  { id: 'whatsapp',     label: 'WhatsApp Messages', icon: WhatsAppIcon, adminOnly: true },
];

// Label for the compact top bar (App.js).
export function sectionLabel(id) {
  if (id === 'configuracion') return 'Configuración';
  return navItems.find(item => item.id === id)?.label || 'másquemuros';
}

function ChartIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="17" x2="17" y2="17" />
      <rect x="4" y="10" width="3" height="6" rx="0.6" />
      <rect x="9" y="6" width="3" height="10" rx="0.6" />
      <rect x="14" y="3" width="3" height="13" rx="0.6" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3h6a3 3 0 013 3v11a3 3 0 00-3-3H3V3z" />
      <path d="M17 3h-6a3 3 0 00-3 3v11a3 3 0 013-3h6V3z" />
    </svg>
  );
}

function EtiquetasIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 2H4a2 2 0 00-2 2v5l9 9a2 2 0 002.8 0l4.2-4.2a2 2 0 000-2.8L9 2z" />
      <circle cx="6.5" cy="6.5" r="1.2" fill="currentColor" />
    </svg>
  );
}

function MetradosIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="14" height="14" rx="2" />
      <line x1="3" y1="8" x2="17" y2="8" />
      <line x1="8" y1="3" x2="8" y2="17" />
      <line x1="11" y1="11" x2="14" y2="11" />
      <line x1="11" y1="14" x2="14" y2="14" />
    </svg>
  );
}

function JournalIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="14" height="14" rx="2" />
      <line x1="3" y1="8" x2="17" y2="8" />
      <line x1="7" y1="2" x2="7" y2="6" />
      <line x1="13" y1="2" x2="13" y2="6" />
      <circle cx="7" cy="12" r="1" fill="currentColor" />
      <circle cx="11" cy="12" r="1" fill="currentColor" />
      <circle cx="7" cy="15" r="1" fill="currentColor" />
    </svg>
  );
}

function SettingsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="10" r="2.5" />
      <path d="M16.5 10a6.5 6.5 0 00-.1-1.1l1.4-1.1-1.5-2.6-1.7.6a6.5 6.5 0 00-1.9-1.1L12.4 3h-3l-.3 1.7a6.5 6.5 0 00-1.9 1.1l-1.7-.6-1.5 2.6 1.4 1.1A6.5 6.5 0 003.5 10c0 .4 0 .7.1 1.1l-1.4 1.1 1.5 2.6 1.7-.6a6.5 6.5 0 001.9 1.1l.3 1.7h3l.3-1.7a6.5 6.5 0 001.9-1.1l1.7.6 1.5-2.6-1.4-1.1c.1-.4.1-.7.1-1.1z" />
    </svg>
  );
}

function EquipoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="3" />
      <circle cx="14" cy="8" r="2.5" />
      <path d="M2 17c0-2.5 2.2-4.5 5-4.5s5 2 5 4.5" />
      <path d="M12 17c0-2 1.5-3.5 3.5-3.5S19 15 19 17" />
    </svg>
  );
}

function LeadsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 3.5h15l-5.8 6.6v5.2l-3.4 1.7v-6.9z" />
    </svg>
  );
}

function DashboardIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="7" height="8" rx="1.5" />
      <rect x="11" y="2" width="7" height="5" rx="1.5" />
      <rect x="2" y="12" width="7" height="6" rx="1.5" />
      <rect x="11" y="9" width="7" height="9" rx="1.5" />
    </svg>
  );
}

function GanttIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <line x1="2" y1="4" x2="12" y2="4" />
      <line x1="5" y1="8" x2="16" y2="8" />
      <line x1="3" y1="12" x2="14" y2="12" />
      <line x1="6" y1="16" x2="18" y2="16" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="14" height="14" rx="2" />
      <line x1="3" y1="8" x2="17" y2="8" />
      <line x1="7" y1="2" x2="7" y2="6" />
      <line x1="13" y1="2" x2="13" y2="6" />
      <rect x="6" y="11" width="3" height="3" rx="0.5" fill="currentColor" stroke="none" />
      <rect x="11" y="11" width="3" height="3" rx="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 17l1.2-3.6A7 7 0 1114.4 4.6 7 7 0 016.6 13.4L3 17z" />
      <path d="M7.5 9.5c.5 1 1.5 2 2.5 2.5l1-1a.5.5 0 01.6-.1l1.5.7a.5.5 0 01.3.5v1a1 1 0 01-1 1c-2.5 0-5-1.5-6.5-3.5S4 6.5 4.5 4.5a1 1 0 011-1h1a.5.5 0 01.5.3l.7 1.5a.5.5 0 01-.1.6l-1 1z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 17H5a2 2 0 01-2-2V5a2 2 0 012-2h3" />
      <path d="M13 14l4-4-4-4" />
      <path d="M17 10H8" />
    </svg>
  );
}

function getInitials(name, email) {
  if (name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (email) return email.slice(0, 2).toUpperCase();
  return '??';
}

// variant="drawer" (compact viewports only): fixed slide-in panel with a
// backdrop. Without it the sidebar renders exactly as on desktop.
export default function Sidebar({ active, onNavigate, variant, open = false, onClose }) {
  const isDrawer = variant === 'drawer';
  const { user } = useUser();
  const { signOut } = useClerk();
  const { role: whitelistRole, isAdmin, loading: roleLoading } = useWhitelistRole();
  const { config: studioConfig, refresh: refreshStudio } = useStudioConfig();
  const [logoMenuOpen, setLogoMenuOpen] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoHover, setLogoHover] = useState(false);
  const logoAreaRef = useRef(null);
  const logoFileInput = useRef(null);

  useEffect(() => {
    if (!isDrawer || !open) return undefined;
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isDrawer, open, onClose]);

  useEffect(() => {
    if (!logoMenuOpen) return;
    function onDoc(e) {
      if (logoAreaRef.current && !logoAreaRef.current.contains(e.target)) setLogoMenuOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setLogoMenuOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [logoMenuOpen]);

  async function handleLogoFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    setLogoBusy(true);
    setLogoMenuOpen(false);
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const path = `logo/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from(ASSETS_BUCKET).upload(path, file, {
      cacheControl: '3600', upsert: false, contentType: file.type,
    });
    if (upErr) { setLogoBusy(false); alert('Error subiendo logo: ' + upErr.message); return; }
    const { data: pub } = supabase.storage.from(ASSETS_BUCKET).getPublicUrl(path);
    const logo_url = pub?.publicUrl || null;
    const existingId = studioConfig?.id;
    let resp;
    if (existingId) {
      resp = await supabase.from('configuracion_estudio').update({ logo_url }).eq('id', existingId).select();
    } else {
      resp = await supabase.from('configuracion_estudio').insert([{ logo_url }]).select();
    }
    setLogoBusy(false);
    if (resp.error) { alert('Error guardando logo: ' + resp.error.message); return; }
    if (!resp.data || resp.data.length === 0) {
      alert('No se actualizó (posible bloqueo RLS en configuracion_estudio)');
      return;
    }
    applyStudioConfig(resp.data[0]);
    refreshStudio();
  }

  async function handleLogoDelete() {
    if (!studioConfig?.logo_url) { setLogoMenuOpen(false); return; }
    setLogoBusy(true);
    setLogoMenuOpen(false);
    const marker = `/${ASSETS_BUCKET}/`;
    const idx = studioConfig.logo_url.indexOf(marker);
    if (idx >= 0) {
      const path = studioConfig.logo_url.slice(idx + marker.length).split('?')[0];
      const { error: remErr } = await supabase.storage.from(ASSETS_BUCKET).remove([path]);
      if (remErr) console.warn('[studio-config] storage.remove failed:', remErr);
    }
    const { data, error } = await supabase
      .from('configuracion_estudio')
      .update({ logo_url: null })
      .eq('id', studioConfig.id)
      .select();
    setLogoBusy(false);
    if (error) { alert('Error: ' + error.message); return; }
    if (!data || data.length === 0) {
      alert('No se actualizó (posible bloqueo RLS en configuracion_estudio)');
      return;
    }
    applyStudioConfig(data[0]);
    refreshStudio();
  }

  const logoUrl = studioConfig?.logo_url || null;
  const studioName = studioConfig?.nombre_estudio || 'másquemuros';

  const displayName =
    user?.fullName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.primaryEmailAddress?.emailAddress ||
    'Usuario';
  const email = user?.primaryEmailAddress?.emailAddress || '';
  const displayRole = roleLoading
    ? '…'
    : whitelistRole === 'admin'
      ? 'Administrador'
      : (whitelistRole === 'member' || whitelistRole === 'miembro')
        ? 'Miembro del equipo'
        : whitelistRole || 'Miembro del equipo';
  const initials = getInitials(user?.fullName, email);
  const imageUrl = user?.imageUrl;

  const navItemStyle = isDrawer ? { ...styles.navItem, ...styles.navItemDrawer } : styles.navItem;

  const aside = (
    <aside
      style={isDrawer
        ? {
          ...styles.sidebar,
          ...styles.drawer,
          transform: open ? 'translateX(0)' : 'translateX(-105%)',
          visibility: open ? 'visible' : 'hidden',
        }
        : styles.sidebar}
      aria-hidden={isDrawer ? !open : undefined}
    >
      <div
        style={styles.logoArea}
        ref={logoAreaRef}
        onMouseEnter={() => setLogoHover(true)}
        onMouseLeave={() => setLogoHover(false)}
      >
        {logoUrl ? (
          <div style={styles.logoImageWrap}>
            <img src={logoUrl} alt={studioName} style={styles.logoImage} />
          </div>
        ) : (
          <div style={styles.logoMark}>{studioName.slice(0, 1).toLowerCase()}</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={styles.logoTitle}>{studioName}</h1>
          <span style={styles.logoSub}>architecture studio</span>
        </div>

        {isAdmin && (logoHover || logoMenuOpen || isDrawer) && (
          <button
            type="button"
            onClick={() => setLogoMenuOpen(v => !v)}
            disabled={logoBusy}
            style={isDrawer ? { ...styles.logoEditBtn, ...styles.logoEditBtnDrawer } : styles.logoEditBtn}
            title={logoBusy ? 'Trabajando…' : 'Cambiar o eliminar logo'}
            aria-label="Editar logo"
            aria-haspopup="menu"
            aria-expanded={logoMenuOpen}
          >
            {logoBusy ? '…' : <PencilIcon />}
          </button>
        )}

        {isAdmin && logoMenuOpen && (
          <div style={styles.logoMenu} role="menu">
            <button
              type="button"
              style={styles.logoMenuItem}
              onClick={() => logoFileInput.current?.click()}
              role="menuitem"
            >
              Cambiar logo
            </button>
            {logoUrl && (
              <button
                type="button"
                style={{ ...styles.logoMenuItem, ...styles.logoMenuItemDanger }}
                onClick={handleLogoDelete}
                role="menuitem"
              >
                Eliminar logo
              </button>
            )}
          </div>
        )}

        <input
          ref={logoFileInput}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp,image/*"
          onChange={handleLogoFile}
          style={{ display: 'none' }}
        />
      </div>

      <nav style={styles.nav}>
        <div style={styles.navLabel}>Menu</div>
        {navItems.filter(item => !item.adminOnly || isAdmin).map(item => {
          const isActive = active === item.id;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              style={{
                ...navItemStyle,
                ...(isActive ? styles.navItemActive : {}),
              }}
              onMouseEnter={e => {
                if (!isActive) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                }
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span style={{
                ...styles.iconWrap,
                ...(isActive ? styles.iconWrapActive : {}),
              }}>
                <Icon />
              </span>
              <span style={styles.navText}>{item.label}</span>
              {isActive && <span style={styles.activeIndicator} />}
            </button>
          );
        })}

        {isAdmin && (
          <>
            <div style={{ ...styles.navLabel, marginTop: 20 }}>Administración</div>
            <button
              onClick={() => onNavigate('configuracion')}
              style={{
                ...navItemStyle,
                ...(active === 'configuracion' ? styles.navItemActive : {}),
              }}
              onMouseEnter={e => {
                if (active !== 'configuracion') e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              }}
              onMouseLeave={e => {
                if (active !== 'configuracion') e.currentTarget.style.background = 'transparent';
              }}
            >
              <span style={{
                ...styles.iconWrap,
                ...(active === 'configuracion' ? styles.iconWrapActive : {}),
              }}>
                <SettingsIcon />
              </span>
              <span style={styles.navText}>Configuración</span>
              {active === 'configuracion' && <span style={styles.activeIndicator} />}
            </button>
          </>
        )}
      </nav>

      <div style={styles.footer}>
        <div style={styles.divider} />
        <div style={styles.footerContent}>
          {imageUrl ? (
            <img src={imageUrl} alt={displayName} style={styles.avatarImg} />
          ) : (
            <div style={styles.avatar}>{initials}</div>
          )}
          <div style={styles.userInfo}>
            <div style={styles.userName}>{displayName}</div>
            <div style={styles.userRole}>{displayRole}</div>
          </div>
          <button
            type="button"
            onClick={() => signOut()}
            title="Cerrar sesión"
            aria-label="Cerrar sesión"
            style={isDrawer ? { ...styles.logoutBtn, ...styles.logoutBtnDrawer } : styles.logoutBtn}
            onMouseEnter={e => {
              e.currentTarget.style.background = 'rgba(201,169,110,0.15)';
              e.currentTarget.style.color = '#C9A96E';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
            }}
          >
            <LogoutIcon />
          </button>
        </div>
      </div>
    </aside>
  );

  if (!isDrawer) return aside;
  return (
    <>
      {open && <div style={styles.drawerBackdrop} onClick={onClose} aria-hidden="true" />}
      {aside}
    </>
  );
}

const styles = {
  // ── Drawer variant (compact viewports only) ──
  drawer: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    height: '100%',
    width: 'min(300px, 85vw)',
    minWidth: 0,
    zIndex: 1100,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    paddingTop: 'env(safe-area-inset-top)',
    paddingBottom: 'env(safe-area-inset-bottom)',
    paddingLeft: 'env(safe-area-inset-left)',
    boxShadow: '8px 0 30px rgba(0,0,0,0.3)',
    transition: 'transform 0.25s ease, visibility 0.25s',
  },
  drawerBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 1099,
    background: 'rgba(20,16,18,0.5)',
  },
  navItemDrawer: {
    minHeight: 44,
    fontSize: 15,
  },
  logoEditBtnDrawer: {
    width: 44,
    height: 44,
    top: 26,
    right: 12,
  },
  logoutBtnDrawer: {
    width: 44,
    height: 44,
  },
  sidebar: {
    width: 260,
    minWidth: 260,
    height: '100vh',
    background: 'linear-gradient(180deg, #6B1C2A 0%, #4A1220 100%)',
    display: 'flex',
    flexDirection: 'column',
    color: '#fff',
    position: 'sticky',
    top: 0,
  },
  logoArea: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '28px 24px 32px',
    position: 'relative',
  },
  logoImageWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: 'rgba(255,255,255,0.08)',
    border: '1.5px solid rgba(201,169,110,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  logoImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block',
  },
  logoEditBtn: {
    position: 'absolute',
    top: 20,
    right: 18,
    width: 24,
    height: 24,
    borderRadius: 8,
    border: '1px solid rgba(201,169,110,0.3)',
    background: 'rgba(0,0,0,0.3)',
    color: '#C9A96E',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    zIndex: 2,
  },
  logoMenu: {
    position: 'absolute',
    top: 48,
    right: 18,
    minWidth: 170,
    background: '#fff',
    color: '#2A2A2A',
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 12px 28px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    zIndex: 10,
  },
  logoMenuItem: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '10px 14px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "'DM Sans', sans-serif",
    color: '#2A2A2A',
  },
  logoMenuItemDanger: { color: '#B83B3B' },
  logoMark: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: 'rgba(201,169,110,0.2)',
    border: '1.5px solid rgba(201,169,110,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 22,
    fontWeight: 700,
    color: '#C9A96E',
    fontStyle: 'italic',
  },
  logoTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: 18,
    fontWeight: 600,
    letterSpacing: '0.02em',
    margin: 0,
    lineHeight: 1.2,
  },
  logoSub: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.15em',
    color: 'rgba(255,255,255,0.45)',
    fontWeight: 500,
  },
  nav: {
    flex: 1,
    padding: '0 12px',
  },
  navLabel: {
    fontSize: 10,
    textTransform: 'uppercase',
    letterSpacing: '0.15em',
    color: 'rgba(255,255,255,0.35)',
    padding: '0 12px 10px',
    fontWeight: 600,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    padding: '11px 12px',
    border: 'none',
    background: 'transparent',
    color: 'rgba(255,255,255,0.6)',
    cursor: 'pointer',
    borderRadius: 10,
    fontSize: 13,
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 500,
    transition: 'all 0.2s ease',
    position: 'relative',
    textAlign: 'left',
  },
  navItemActive: {
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.05)',
    flexShrink: 0,
  },
  iconWrapActive: {
    background: 'rgba(201,169,110,0.2)',
    color: '#C9A96E',
  },
  navText: {
    flex: 1,
  },
  activeIndicator: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: '#C9A96E',
  },
  footer: {
    padding: '0 24px 24px',
  },
  divider: {
    height: 1,
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 20,
  },
  footerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'rgba(201,169,110,0.2)',
    border: '1.5px solid rgba(201,169,110,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 600,
    color: '#C9A96E',
    flexShrink: 0,
  },
  avatarImg: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '1.5px solid rgba(201,169,110,0.3)',
    flexShrink: 0,
  },
  userInfo: {
    flex: 1,
    minWidth: 0,
  },
  userName: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userRole: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.4)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  logoutBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.05)',
    border: 'none',
    color: 'rgba(255,255,255,0.6)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flexShrink: 0,
  },
};
